// components/entities/pages/OrganisationChartTab.tsx
import React, { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { AlertTriangle, Search } from '@/components/ui/lucide-memo';
import { RowMoreButton } from '@/components/ui/RowMoreButton';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { hydrateOrganisationMembersInventory } from './organisation-teams/model';
import type {
  LeasingUserDTO,
  OrganisationChartRuntimeState,
  MemberData,
  OrganisationRuntimeState,
} from '#types/types';
import UserAvatar from '@/components/UserAvatar';
import { useOrganisationMemberActions } from '@/components/organisation/useOrganisationMemberActions';

interface OrganisationChartTabProps {
  organisationId: string;
}

interface UserTreeNode {
  user: LeasingUserDTO;
  children: UserTreeNode[];
  cyclic?: boolean;
}

/** One row: a node flattened out of the forest, keeping its depth for indentation. */
interface MemberRow {
  user: LeasingUserDTO;
  depth: number;
  directReports: number;
  cyclic: boolean;
}

const SORT_OPTIONS = [
  { label: 'Hierarchy', field: 'hierarchy' },
  { label: 'Name', field: 'name' },
  { label: 'Reports', field: 'reports' },
  { label: 'Projects', field: 'projects' },
  { label: 'Generations', field: 'generations' },
];

const getUserDisplayName = (user: LeasingUserDTO): string => {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return fullName || user.email;
};

const sortUsers = (left: LeasingUserDTO, right: LeasingUserDTO): number => {
  const leftName = getUserDisplayName(left).toLowerCase();
  const rightName = getUserDisplayName(right).toLowerCase();
  return leftName.localeCompare(rightName);
};

const selectOrganisationChartRuntime = (
  organisationId: string,
  registry: Record<string, unknown>
): OrganisationChartRuntimeState | null => {
  const organisation = registry[organisationId] as
    | { chart?: OrganisationChartRuntimeState; runtime?: OrganisationRuntimeState }
    | undefined;
  return organisation?.runtime?.chart ?? organisation?.chart ?? null;
};

export function OrganisationChartTab({ organisationId }: OrganisationChartTabProps) {
  const { kernel } = useKernel();
  const userContext = useKernelStore((state) => state.userContext);
  const userRegistry = useKernelStore((state) => state.inventory.User.registry);
  const chartRuntime = useKernelStore((state) =>
    selectOrganisationChartRuntime(organisationId, state.inventory.Organisation.registry)
  );

  // Entity pages hold no local useState (AGENTS.md 4.2). Search and sort live in the
  // Organisation chart runtime overlay, so they survive a socket update and a remount.
  const patchChart = useCallback(
    (
      patch:
        | Partial<OrganisationChartRuntimeState>
        | ((current: OrganisationChartRuntimeState) => Partial<OrganisationChartRuntimeState>)
    ) => {
      if (!kernel.current) return;
      const runtime = kernel.current.readEntityRuntimeState(
        'Organisation',
        organisationId
      ) as Partial<OrganisationRuntimeState>;
      // Read through the kernel rather than render scope, so a rapid second call cannot
      // resolve against a stale closure (AGENTS.md 4.4).
      const currentChart = runtime.chart ?? {};
      const next = typeof patch === 'function' ? patch(currentChart) : patch;
      kernel.current.patchEntityRuntimeState('Organisation', organisationId, {
        chart: { ...currentChart, ...next },
      });
    },
    [kernel, organisationId]
  );

  const search = chartRuntime?.memberSearch ?? '';
  const sortField = chartRuntime?.memberSortField ?? 'hierarchy';
  const sortDir = chartRuntime?.memberSortDir ?? 'asc';

  const users = useMemo(() => {
    return Object.values(userRegistry)
      .filter((user): user is LeasingUserDTO => Boolean(user && user.organisationId === organisationId))
      .sort(sortUsers);
  }, [organisationId, userRegistry]);

  const members = useMemo(() => users as MemberData[], [users]);

  // Unchanged from the chart: reporting lines come from managerId, roots are members whose manager
  // is absent or outside this org, cycles are detected per path, and members never reached from a
  // root are appended so a pure cycle component still lists.
  const forest = useMemo(() => {
    const byId = new Map<string, LeasingUserDTO>(users.map((user) => [user.id, user]));
    const childrenByManager = new Map<string, string[]>();

    users.forEach((user) => {
      const managerId = user.managerId ?? null;
      if (!managerId || !byId.has(managerId)) return;
      const bucket = childrenByManager.get(managerId) ?? [];
      bucket.push(user.id);
      childrenByManager.set(managerId, bucket);
    });

    childrenByManager.forEach((childIds, managerId) => {
      childIds.sort((leftId, rightId) => {
        const left = byId.get(leftId);
        const right = byId.get(rightId);
        if (!left || !right) return 0;
        return sortUsers(left, right);
      });
      childrenByManager.set(managerId, childIds);
    });

    const rootIds = users
      .filter((user) => !user.managerId || !byId.has(user.managerId))
      .map((user) => user.id);

    const visitedGlobal = new Set<string>();
    const buildNode = (userId: string, path: Set<string>): UserTreeNode | null => {
      const user = byId.get(userId);
      if (!user) return null;
      if (path.has(userId)) return { user, children: [], cyclic: true };

      visitedGlobal.add(userId);
      const nextPath = new Set(path);
      nextPath.add(userId);

      const childIds = childrenByManager.get(userId) ?? [];
      const children = childIds
        .map((childId) => buildNode(childId, nextPath))
        .filter((child): child is UserTreeNode => Boolean(child));

      return { user, children };
    };

    const roots = rootIds
      .map((rootId) => buildNode(rootId, new Set()))
      .filter((root): root is UserTreeNode => Boolean(root));

    users
      .map((user) => user.id)
      .filter((userId) => !visitedGlobal.has(userId))
      .forEach((detachedId) => {
        const detachedRoot = buildNode(detachedId, new Set());
        if (detachedRoot) roots.push(detachedRoot);
      });

    return roots;
  }, [users]);

  /** Depth-first walk, so a manager is always immediately followed by their own reports. */
  const hierarchyRows = useMemo(() => {
    const rows: MemberRow[] = [];
    const walk = (node: UserTreeNode, depth: number) => {
      rows.push({
        user: node.user,
        depth,
        directReports: node.children.length,
        cyclic: Boolean(node.cyclic),
      });
      node.children.forEach((child) => walk(child, depth + 1));
    };
    forest.forEach((root) => walk(root, 0));
    return rows;
  }, [forest]);

  const managerNameById = useMemo(
    () => new Map(users.map((user) => [user.id, getUserDisplayName(user)])),
    [users]
  );

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matched = term
      ? hierarchyRows.filter((row) =>
          `${getUserDisplayName(row.user)} ${row.user.email ?? ''}`.toLowerCase().includes(term)
        )
      : hierarchyRows;

    // Indentation only reads correctly while rows stay a contiguous tree, so any other sort, or a
    // search that breaks parent/child adjacency, lists members flat instead.
    if (sortField === 'hierarchy' && !term) return matched;

    const direction = sortDir === 'asc' ? 1 : -1;
    return matched
      .map((row) => ({ ...row, depth: 0 }))
      .sort((a, b) => {
        switch (sortField) {
          case 'reports':
            return (a.directReports - b.directReports) * direction;
          case 'projects':
            return ((a.user.projectCount ?? 0) - (b.user.projectCount ?? 0)) * direction;
          case 'generations':
            return ((a.user.generationCount ?? 0) - (b.user.generationCount ?? 0)) * direction;
          default:
            return getUserDisplayName(a.user).localeCompare(getUserDisplayName(b.user)) * direction;
        }
      });
  }, [hierarchyRows, search, sortField, sortDir]);

  useEffect(() => {
    if (!kernel.current || !organisationId) return;
    void hydrateOrganisationMembersInventory(kernel, organisationId);
  }, [kernel, organisationId]);

  const selectedUserId = chartRuntime?.selectedUserId ?? null;

  const handleSelectUser = useCallback(
    (userId: string) => {
      patchChart((current) => ({
        selectedUserId: current.selectedUserId === userId ? null : userId,
      }));
    },
    [patchChart]
  );

  const handleSearchChange = useCallback(
    (value: string) => patchChart({ memberSearch: value }),
    [patchChart]
  );

  const isMemberCurrentUser = useCallback(
    (member: MemberData) => member.id === userContext.dbUserId,
    [userContext.dbUserId]
  );

  // Every mutation on this tab (re-parent, promote, delete, change email, permissions, avatar,
  // team report) lives in this shared menu. Rows open it on right-click, as the chart cards did.
  const { avatarTimestamp, openMemberContextMenu, memberActionModals } = useOrganisationMemberActions({
    organisationId,
    refreshOrganisationId: organisationId,
    members,
    isFaterAdmin: userContext.isFaterAdmin,
    canDelete: userContext.isOrganisationAdmin || userContext.isFaterAdmin,
    canManageSubordinates: userContext.isOrganisationAdmin || userContext.isFaterAdmin,
    isCurrentUser: isMemberCurrentUser,
  });

  const handleSortChange = useCallback(
    (field: string) => {
      patchChart((current) => {
        const currentField = current.memberSortField ?? 'hierarchy';
        const currentDir = current.memberSortDir ?? 'asc';
        return {
          memberSortField: field,
          memberSortDir: field === currentField ? (currentDir === 'asc' ? 'desc' : 'asc') : 'asc',
        };
      });
    },
    [patchChart]
  );

  if (users.length === 0) {
    return <p className="py-3 text-[#8E8E8E]" style={{ fontSize: 15 }}>No members found for this organisation.</p>;
  }

  // ChatGPT's list (owner, 2026-09-29: 'chatgptize the hierarchy'): the reporting tree as indented
  // rows of people, each with one grey line and a visible '...' for the member menu that used to
  // hide behind a right-click. Search and sort sit above as a pill and a borderless picker.
  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center gap-2 pb-3">
        <label className="flex h-9 min-w-0 flex-1 cursor-text items-center gap-3 rounded-full bg-[#212121] px-3 text-[#8E8E8E]">
          <Search size={18} strokeWidth={1.6} className="shrink-0" />
          <input
            type="search"
            value={search}
            onChange={(event) => handleSearchChange(event.target.value)}
            placeholder="Search members"
            aria-label="Search members"
            className="min-w-0 flex-1 bg-transparent text-[#ECECEC] outline-none placeholder:text-[#8E8E8E]"
            style={{ fontSize: 15 }}
          />
        </label>
        <select
          value={sortField}
          onChange={(event) => handleSortChange(event.target.value)}
          aria-label="Sort members"
          className="h-9 shrink-0 cursor-pointer rounded-full bg-transparent px-3 text-[#ECECEC] outline-none transition-colors hover:bg-[#212121]"
          style={{ fontSize: 15 }}
        >
          {SORT_OPTIONS.map((option) => <option key={option.field} value={option.field}>{option.label}</option>)}
        </select>
      </div>
      <p className="pb-2 text-[#8E8E8E]" style={{ fontSize: 14 }}>
        {visibleRows.length === users.length ? `${users.length} members` : `${visibleRows.length} of ${users.length} members`}
        {' · '}Reporting lines decide what each manager can see.
      </p>

      {visibleRows.length === 0 ? (
        <p className="py-3 text-[#8E8E8E]" style={{ fontSize: 15 }}>No member matches.</p>
      ) : (
        // The rows' hover pad hangs outside the column so the photos sit on the page's left edge.
        <div className="-mx-2 flex flex-col">
          {visibleRows.map((row) => {
            const name = getUserDisplayName(row.user);
            const managerName = row.user.managerId ? managerNameById.get(row.user.managerId) : null;
            const isSelected = row.user.id === selectedUserId;
            const details = [
              row.user.email,
              row.user.isOrganisationAdmin ? 'Admin' : null,
              // Flat lists lose the indentation, so they say who the member reports to instead.
              row.depth === 0 && managerName ? `reports to ${managerName}` : null,
              row.directReports ? `${row.directReports} ${row.directReports === 1 ? 'report' : 'reports'}` : null,
              `${row.user.projectCount ?? 0} projects`,
              `${(row.user.generationCount ?? 0).toLocaleString('en-US')} generations`,
            ].filter(Boolean).join(' · ');
            return (
              <div
                key={row.user.id}
                className={clsx(
                  'group flex min-h-[56px] cursor-default items-center gap-3 rounded-[12px] pr-1 transition-colors',
                  isSelected ? 'bg-[#212121]' : 'hover:bg-[#1A1A1A]',
                )}
                style={{ paddingLeft: 8 + row.depth * 28 }}
                onClick={() => handleSelectUser(row.user.id)}
                onContextMenu={(event) => openMemberContextMenu(row.user as MemberData, event)}
              >
                <UserAvatar userId={row.user.id} name={name} canEdit={false} size={32} externalTimestamp={avatarTimestamp} />
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-1.5 text-[#ECECEC]" style={{ fontSize: 15, lineHeight: '21px' }}>
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap" style={{ fontSize: 'inherit' }} title={name}>{name}</span>
                    {row.cyclic ? <AlertTriangle size={14} className="shrink-0 text-[#8E8E8E]" aria-label="Reporting loop" /> : null}
                  </div>
                  <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[#8E8E8E]" style={{ fontSize: 14, lineHeight: '20px' }} title={details}>{details}</div>
                </div>
                <RowMoreButton
                  label={`Manage ${name}`}
                  onClick={(event) => { event.stopPropagation(); openMemberContextMenu(row.user as MemberData, event); }}
                />
              </div>
            );
          })}
        </div>
      )}
      {memberActionModals}
    </div>
  );
}
